import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import api, { API_BASE } from '../services/api';
import { trackOptionEvent } from '../services/metrics';
import { useToast } from '../contexts/ToastContext';
import WizardProgress from '../components/wizard/WizardProgress';
import UploadStep from '../components/wizard/UploadStep';
import SizeSelectionStep from '../components/wizard/SizeSelectionStep';
import CropStep from '../components/wizard/CropStep';
import ReviewStep from '../components/wizard/ReviewStep';
import { ExtendedSize, DpiWarning, ImageDimensions, MATERIALS, FINISHES } from '../components/wizard/types';
import { validateFile } from '../constants/fileUpload';
import './CustomPrintWizard.css';

const CustomPrintWizard: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [image, setImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({ unit: '%', x: 5, y: 5, width: 90, height: 90 });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedSizeId, setSelectedSizeId] = useState<number | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<string>(MATERIALS[0]);
  const [selectedFinish, setSelectedFinish] = useState<string>(FINISHES[0].value);
  const [borderColor, setBorderColor] = useState<string>('#ffffff');
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const [displayedImageDimensions, setDisplayedImageDimensions] = useState<ImageDimensions | null>(null);
  const [naturalImageDimensions, setNaturalImageDimensions] = useState<ImageDimensions | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [dpiWarning, setDpiWarning] = useState<DpiWarning | null>(null);
  const [calculatedDpi, setCalculatedDpi] = useState<number | null>(null);
  const [sizes, setSizes] = useState<ExtendedSize[]>([]);
  const [loadingSizes, setLoadingSizes] = useState<boolean>(true);
  const [previewCanvasUrl, setPreviewCanvasUrl] = useState<string | null>(null);
  const [aspectRatios, setAspectRatios] = useState<Array<{ id: number; name: string; ratio: number }>>([]);
  const [selectedAspectRatioId, setSelectedAspectRatioId] = useState<number | null>(null);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<number>(1); // Default to 1:1
  const { success, error: showError, warning } = useToast();
  const imgRef = useRef<HTMLImageElement>(null);

  const totalSteps = 4;
  const stepLabels = ['Upload', 'Crop', 'Size', 'Review'];

  // Fetch aspect ratios and sizes from backend
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        // Fetch aspect ratios - they contain sizes with pricing
        const aspectRatiosRes = await api.get<Array<any>>('/AspectRatios').catch(() => ({ data: [] }));
        
        // Set aspect ratios - filter out those without sizes configured
        const aspectRatiosData = Array.isArray(aspectRatiosRes.data) 
          ? aspectRatiosRes.data 
          : (aspectRatiosRes.data as any)?.data || [];
        
        // Filter out aspect ratios without sizes configured
        const arData = aspectRatiosData
          .filter((ar: any) => {
            const hasSizes = (ar.defaultSizes && ar.defaultSizes.length > 0) || 
                           (ar.additionalSizes && ar.additionalSizes.length > 0);
            if (!hasSizes) {
              console.log(`[CustomPrintWizard] Filtering out aspect ratio ${ar.id} (${ar.name}) - no sizes configured`);
            }
            return hasSizes;
          })
          .map((ar: any) => ({
            id: ar.id,
            name: ar.name,
            ratio: ar.ratio || 1
          }));
        setAspectRatios(arData);
        
        // Set default aspect ratio if available
        if (arData.length > 0 && !selectedAspectRatioId) {
          setSelectedAspectRatioId(arData[0].id);
          setSelectedAspectRatio(arData[0].ratio);
        }
        
        // Build sizes from aspect ratios - sizes come from aspect ratios, not from /sizes endpoint
        // Each aspect ratio contains sizes with prices (from Size.Price, can be overridden per aspect ratio)
        const allSizesMap = new Map<number, ExtendedSize>();
        
        aspectRatiosData.forEach((ar: any) => {
          // Combine default and additional sizes
          const allArSizes = [
            ...(ar.defaultSizes || []),
            ...(ar.additionalSizes || [])
          ];
          
          allArSizes.forEach((ars: any) => {
            if (ars.size && !allSizesMap.has(ars.size.id)) {
              // Price comes from size, can be overridden per aspect ratio
              const price = ars.effectivePrice || ars.size.price || ars.priceOverride || 0;
              
              allSizesMap.set(ars.size.id, {
                id: ars.size.id,
                name: ars.size.name || `${ars.size.width}x${ars.size.height}`, // Add name property
                width: ars.size.width,
                height: ars.size.height,
                ratio: ars.size.ratio || parseFloat(ars.size.ratio),
                price: price,
                portrait: {
                  label: `${ars.size.width}x${ars.size.height}`,
                  value: `${ars.size.width}x${ars.size.height}`,
                  price: price,
                  aspectRatio: parseFloat(ars.size.ratio) || (ars.size.width / ars.size.height)
                },
                landscape: ars.size.width !== ars.size.height ? {
                  label: `${ars.size.height}x${ars.size.width}`,
                  value: `${ars.size.height}x${ars.size.width}`,
                  price: price,
                  aspectRatio: 1 / (parseFloat(ars.size.ratio) || (ars.size.width / ars.size.height))
                } : null
              });
            }
          });
        });
        
        // Convert map to array
        const sizesData: ExtendedSize[] = Array.from(allSizesMap.values());
        setSizes(sizesData);
      } catch (error: any) {
        console.error('Failed to fetch data:', error);
        setSizes([]);
        setAspectRatios([]);
      } finally {
        setLoadingSizes(false);
      }
    };

    fetchData();
  }, [selectedAspectRatioId]);

  // Get the current size object based on selected size and orientation
  const getCurrentSizeObj = useCallback(() => {
    if (!selectedSizeId || !selectedSize) return null;

    const baseSize = sizes.find(s => s.id === selectedSizeId);
    if (!baseSize) return null;

    if (baseSize.landscape && selectedSize === baseSize.landscape.value) {
      return baseSize.landscape;
    }

    return baseSize.portrait;
  }, [selectedSizeId, selectedSize, sizes]);

  // Calculate maximum centered crop for aspect ratio (used when aspect ratio is selected)
  const calculateMaxCenteredCrop = (imgWidth: number, imgHeight: number, aspectRatio: number): Crop => {
    const imgAspect = imgWidth / imgHeight;
    let cropWidth: number;
    let cropHeight: number;

    if (imgAspect > aspectRatio) {
      // Image is wider than crop aspect - crop width to match (use full height)
      cropHeight = 100;
      cropWidth = (100 * aspectRatio) / imgAspect;
    } else {
      // Image is taller than crop aspect - crop height to match (use full width)
      cropWidth = 100;
      cropHeight = (100 * imgAspect) / aspectRatio;
    }

    return {
      unit: '%',
      x: (100 - cropWidth) / 2,
      y: (100 - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight
    };
  };

  // Handle aspect ratio change
  const handleAspectRatioChange = (aspectRatioId: number, ratio: number): void => {
    setSelectedAspectRatioId(aspectRatioId);
    setSelectedAspectRatio(ratio);
    
    // Calculate max centered crop if image is loaded
    if (imgRef.current) {
      const img = imgRef.current;
      const naturalWidth = img.naturalWidth || img.width;
      const naturalHeight = img.naturalHeight || img.height;
      
      if (naturalWidth > 0 && naturalHeight > 0) {
        const maxCrop = calculateMaxCenteredCrop(naturalWidth, naturalHeight, ratio);
        setCrop(maxCrop);
      } else {
        // Fallback to full image if dimensions not available yet
        setCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100 });
      }
    } else {
      // Image not loaded yet, default to full image
      setCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100 });
    }
    
    setCompletedCrop(null);
    setCroppedImageUrl((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return null;
    });
  };

  // Initialize crop when entering crop step - default to full image
  useEffect(() => {
    if (currentStep === 2 && image && !selectedAspectRatioId) {
      // Default to full image when first entering crop step
      setCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100 });
    } else if (currentStep === 2 && image && selectedAspectRatio && selectedAspectRatioId && imgRef.current) {
      const img = imgRef.current;
      const naturalWidth = img.naturalWidth || img.width;
      const naturalHeight = img.naturalHeight || img.height;
      
      if (naturalWidth > 0 && naturalHeight > 0) {
        const maxCrop = calculateMaxCenteredCrop(naturalWidth, naturalHeight, selectedAspectRatio);
        setCrop(maxCrop);
        setCompletedCrop(null);
      }
    }
  }, [selectedAspectRatioId, currentStep, image, selectedAspectRatio]);

  const getCroppedImg = useCallback((imageElementOrSrc: HTMLImageElement | string, crop: PixelCrop, displayedWidth: number | null = null, displayedHeight: number | null = null): Promise<string | null> => {
    if (!imageElementOrSrc || !crop || crop.x === undefined || crop.y === undefined || crop.width === undefined || crop.height === undefined) {
      return Promise.resolve(null);
    }
    
    return new Promise((resolve) => {
      let imgElement: HTMLImageElement;
      
      if (typeof imageElementOrSrc === 'string') {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          imgElement = img;
          processCrop();
        };
        img.onerror = () => resolve(null);
        img.src = imageElementOrSrc;
      } else {
        imgElement = imageElementOrSrc;
        processCrop();
      }
      
      function processCrop(): void {
        const canvas = document.createElement('canvas');
        const displayW = displayedWidth || imgElement.width || imgElement.naturalWidth;
        const displayH = displayedHeight || imgElement.height || imgElement.naturalHeight;
        
        const scaleX = displayW > 0 ? imgElement.naturalWidth / displayW : 1;
        const scaleY = displayH > 0 ? imgElement.naturalHeight / displayH : 1;
        
        let cropX: number, cropY: number, cropWidth: number, cropHeight: number;
        
        if (crop.unit === 'px') {
          cropX = crop.x * scaleX;
          cropY = crop.y * scaleY;
          cropWidth = crop.width * scaleX;
          cropHeight = crop.height * scaleY;
        } else {
          cropX = (crop.x / 100) * displayW * scaleX;
          cropY = (crop.y / 100) * displayH * scaleY;
          cropWidth = (crop.width / 100) * displayW * scaleX;
          cropHeight = (crop.height / 100) * displayH * scaleY;
        }
        
        const finalCropX = Math.max(0, Math.min(cropX, imgElement.naturalWidth));
        const finalCropY = Math.max(0, Math.min(cropY, imgElement.naturalHeight));
        const finalCropWidth = Math.max(1, Math.min(cropWidth, imgElement.naturalWidth - finalCropX));
        const finalCropHeight = Math.max(1, Math.min(cropHeight, imgElement.naturalHeight - finalCropY));
        
        if (finalCropWidth <= 0 || finalCropHeight <= 0) {
          resolve(null);
          return;
        }
        
        canvas.width = finalCropWidth;
        canvas.height = finalCropHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imgElement, finalCropX, finalCropY, finalCropWidth, finalCropHeight, 0, 0, finalCropWidth, finalCropHeight);

        canvas.toBlob((blob) => {
          resolve(blob ? URL.createObjectURL(blob) : null);
        }, 'image/jpeg', 0.95);
      }
    });
  }, []);

  const onImageLoaded = useCallback((image: HTMLImageElement): boolean => {
    imgRef.current = image;
    setNaturalImageDimensions({
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height
    });
    return false;
  }, []);

  const onCropComplete = useCallback((crop: PixelCrop): void => {
    if (crop && crop.width && crop.height && crop.x !== undefined && crop.y !== undefined) {
      setCompletedCrop(crop);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file using centralized validation
    const validation = validateFile(file);
    if (!validation.isValid) {
      showError(validation.error || 'Invalid file');
      e.target.value = ''; // Clear the input
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result as string);
      // Crop will be initialized when aspect ratio is selected
    };
    reader.onerror = () => {
      showError('Failed to read file. Please try again.');
      e.target.value = ''; // Clear the input
    };
    reader.readAsDataURL(file);
  };

  const handleSizeSelect = (sizeId: number, sizeValue: string): void => {
    setSelectedSizeId(sizeId);
    setSelectedSize(sizeValue);
    trackOptionEvent({
      itemType: 'CustomPrint',
      size: sizeValue,
      material: selectedMaterial,
      finish: selectedFinish,
      eventType: 'Selected'
    });
    // Don't reset crop - it's already set based on aspect ratio
  };

  // DPI calculation is now handled in SizeSelectionStep component

  // Convert percentage crop to pixel crop
  const convertCropToPixelCrop = useCallback((crop: Crop, imgElement: HTMLImageElement): PixelCrop | null => {
    if (!crop || crop.width === undefined || crop.height === undefined) return null;
    
    const displayedWidth = imgElement.offsetWidth || imgElement.width || imgElement.naturalWidth;
    const displayedHeight = imgElement.offsetHeight || imgElement.height || imgElement.naturalHeight;
    
    if (crop.unit === '%') {
      return {
        x: (crop.x || 0) * displayedWidth / 100,
        y: (crop.y || 0) * displayedHeight / 100,
        width: crop.width * displayedWidth / 100,
        height: crop.height * displayedHeight / 100,
        unit: 'px'
      };
    } else {
      return {
        x: crop.x || 0,
        y: crop.y || 0,
        width: crop.width,
        height: crop.height,
        unit: 'px'
      };
    }
  }, []);

  // Generate cropped image when moving to next step from crop step
  const generateCroppedImage = useCallback(async (): Promise<boolean> => {
    if (!image) return false;

    try {
      const imgElement = imgRef.current || document.querySelector('.crop-container img') as HTMLImageElement;
      if (!imgElement) return false;

      // Always use the current crop state to ensure we get the latest crop
      // Convert current crop to pixel crop (this handles both percentage and pixel crops)
      const pixelCrop = convertCropToPixelCrop(crop, imgElement);
      
      if (!pixelCrop) return false;

      const displayedWidth = imgElement.offsetWidth || imgElement.width || imgElement.naturalWidth;
      const displayedHeight = imgElement.offsetHeight || imgElement.height || imgElement.naturalHeight;
      
      const url = await getCroppedImg(imgElement, pixelCrop, displayedWidth, displayedHeight);
      if (url) {
        setCroppedImageUrl((prevUrl) => {
          if (prevUrl) URL.revokeObjectURL(prevUrl);
          return url;
        });
        setDisplayedImageDimensions({ width: displayedWidth, height: displayedHeight });
        // Always store the pixel crop that was actually used
        setCompletedCrop(pixelCrop);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error generating cropped image:', error);
      return false;
    }
  }, [crop, image, getCroppedImg, convertCropToPixelCrop]);

  const getCurrentPrice = (): number => {
    if (!selectedSizeId) return 0;
    const baseSize = sizes.find(s => s.id === selectedSizeId);
    if (!baseSize) return 0;
    let price = baseSize.price;
    
    const finishObj = FINISHES.find(f => f.value === selectedFinish);
    if (finishObj) {
      const sizeObj = getCurrentSizeObj();
      if (finishObj.frameSurcharge > 0 && sizeObj && (sizeObj.value === '10x20' || sizeObj.value === '20x10')) {
        price += finishObj.frameSurcharge;
      }
      price += finishObj.hangerSurcharge;
    }
    
    return price;
  };

  const handleNext = async (): Promise<void> => {
    if (currentStep === 1 && !image) {
      warning('Please upload an image first.');
      return;
    }
    if (currentStep === 2) {
      if (!selectedAspectRatioId) {
        warning('Please select an aspect ratio.');
        return;
      }
      // Allow proceeding even without manual crop adjustment - will use current crop
      const success = await generateCroppedImage();
      if (!success) {
        showError('Failed to process cropped image. Please try adjusting your crop.');
        return;
      }
    }
    if (currentStep === 3 && !selectedSize) {
      warning('Please select a size.');
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = (): void => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAddToCart = async (): Promise<void> => {
    if (!image || !selectedSize) {
      warning('Please complete all steps.');
      return;
    }
    
    // Generate cropped image if not already done
    if (!croppedImageUrl) {
      const success = await generateCroppedImage();
      if (!success) {
        showError('Failed to process image. Please try again.');
        return;
      }
    }
    
    if (!croppedImageUrl) {
      warning('Please complete all steps and ensure the image is cropped.');
      return;
    }

    setUploading(true);

    try {
      let uploadedImageUrl: string | null = null;
      
      if (croppedImageUrl && !croppedImageUrl.startsWith('blob:') && !croppedImageUrl.startsWith('data:')) {
        uploadedImageUrl = croppedImageUrl;
      } else {
        const response = await fetch(croppedImageUrl);
        if (!response.ok) throw new Error('Failed to fetch cropped image blob');
        const croppedBlob = await response.blob();
        
        const formData = new FormData();
        formData.append('file', croppedBlob, 'cropped-image.jpg');
        const uploadResponse = await api.post<{ imageUrl: string }>('/products/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        uploadedImageUrl = uploadResponse.data.imageUrl;
      }
      
      if (!uploadedImageUrl || uploadedImageUrl.startsWith('blob:') || uploadedImageUrl.startsWith('data:')) {
        throw new Error('Failed to upload cropped image. Please try again.');
      }
      
      const sizeObj = getCurrentSizeObj();
      if (!sizeObj) throw new Error('Invalid size selection');

      const productData = {
        name: `Custom Print - ${selectedSize} ${selectedMaterial}`,
        description: `Custom photo print: ${selectedSize}, ${selectedMaterial}, ${selectedFinish}`,
        price: getCurrentPrice(),
        imageUrl: image,
        croppedImageUrl: uploadedImageUrl,
        aspectRatio: `${selectedAspectRatio}`,
        size: selectedSize,
        material: selectedMaterial,
        finish: selectedFinish,
        isCustomPrint: true,
      };

      const productResponse = await api.post<{ id: number }>('/products', productData);
      await api.post('/cart', { productId: productResponse.data.id, quantity: 1 });
      await trackOptionEvent({
        itemType: 'Product',
        itemId: productResponse.data.id,
        size: selectedSize || undefined,
        material: selectedMaterial,
        finish: selectedFinish,
        eventType: 'Carted'
      });

      success('Added to cart.');
      navigate('/cart');
    } catch (err: any) {
      console.error('Error adding to cart:', err);
      if (err.code === 'ERR_NETWORK' || err.message?.includes('ERR_CONNECTION_REFUSED') || err.message?.includes('Failed to fetch')) {
        showError(`Cannot connect to server. Please make sure the backend is running on ${API_BASE}`);
      } else if (err.response) {
        showError(err.response.data?.message || `Failed to add to cart: ${err.response.statusText}`);
      } else {
        showError('Failed to add to cart. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCropImageLoaded = (img: HTMLImageElement): boolean => {
    onImageLoaded(img);
    const displayW = img.offsetWidth || img.width || img.naturalWidth;
    const displayH = img.offsetHeight || img.height || img.naturalHeight;
    
    setDisplayedImageDimensions({
      width: displayW,
      height: displayH
    });

    setNaturalImageDimensions({
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height
    });
    
    if (completedCrop && completedCrop.x !== undefined && completedCrop.y !== undefined) {
      // PixelCrop always has unit 'px', so convert to percentage-based Crop
      setCrop({
        unit: '%',
        x: (completedCrop.x / displayW) * 100,
        y: (completedCrop.y / displayH) * 100,
        width: (completedCrop.width / displayW) * 100,
        height: (completedCrop.height / displayH) * 100
      });
    } else if (!completedCrop && selectedAspectRatio && selectedAspectRatioId) {
      // Initialize crop with maximum centered crop based on aspect ratio
      const naturalWidth = img.naturalWidth || img.width;
      const naturalHeight = img.naturalHeight || img.height;
      
      if (naturalWidth > 0 && naturalHeight > 0) {
        const maxCrop = calculateMaxCenteredCrop(naturalWidth, naturalHeight, selectedAspectRatio);
        setCrop(maxCrop);
      }
    } else if (!completedCrop && !selectedAspectRatioId) {
      // Default to full image if no aspect ratio selected
      setCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100 });
    }
    return false;
  };

  const renderStepContent = (): React.ReactElement | null => {
    switch (currentStep) {
      case 1:
        return <UploadStep image={image} onFileChange={handleFileChange} />;

      case 2:
        return (
          <CropStep
            image={image}
            crop={crop}
            completedCrop={completedCrop}
            currentAspectRatio={selectedAspectRatio}
            aspectRatios={aspectRatios}
            selectedAspectRatioId={selectedAspectRatioId}
            onAspectRatioChange={handleAspectRatioChange}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onImageLoaded={handleCropImageLoaded}
            imgRef={imgRef as React.RefObject<HTMLImageElement>}
          />
        );

      case 3:
        return (
          <SizeSelectionStep
            sizes={sizes}
            loadingSizes={loadingSizes}
            selectedSizeId={selectedSizeId}
            selectedAspectRatio={selectedAspectRatio}
            completedCrop={completedCrop}
            imgRef={imgRef as React.RefObject<HTMLImageElement>}
            displayedImageDimensions={displayedImageDimensions}
            naturalImageDimensions={naturalImageDimensions}
            calculatedDpi={calculatedDpi}
            dpiWarning={dpiWarning}
            onSizeSelect={handleSizeSelect}
            onDpiCalculated={setCalculatedDpi}
            onDpiWarning={setDpiWarning}
          />
        );

      case 4:
        return (
          <ReviewStep
            image={image}
            croppedImageUrl={croppedImageUrl}
            selectedSize={selectedSize}
            selectedSizeId={selectedSizeId}
            selectedMaterial={selectedMaterial}
            selectedFinish={selectedFinish}
            borderColor={borderColor}
            sizes={sizes}
            getCurrentPrice={getCurrentPrice}
            previewCanvasUrl={previewCanvasUrl}
                onMaterialChange={(material) => {
                  setSelectedMaterial(material);
                  trackOptionEvent({
                    itemType: 'CustomPrint',
                    size: selectedSize || undefined,
                    material,
                    finish: selectedFinish,
                    eventType: 'Selected'
                  });
                }}
                onFinishChange={(finish) => {
                  setSelectedFinish(finish);
                  trackOptionEvent({
                    itemType: 'CustomPrint',
                    size: selectedSize || undefined,
                    material: selectedMaterial,
                    finish,
                    eventType: 'Selected'
                  });
                }}
            onBorderColorChange={setBorderColor}
            onPreviewReady={setPreviewCanvasUrl}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="custom-print-wizard">
      <div className="container">
        <div className="wizard-header">
          <h1 className="wizard-title">Create Your Custom Print</h1>
          <WizardProgress currentStep={currentStep} totalSteps={totalSteps} stepLabels={stepLabels} />
        </div>

        <div className={`wizard-content${currentStep === 1 && image ? ' wizard-content-no-border' : ''}`}>
          {renderStepContent()}
        </div>

        <div className="wizard-actions">
          {currentStep > 1 && (
            <button onClick={handleBack} className="btn btn-outline">
              Back
            </button>
          )}
          {currentStep < totalSteps ? (
            <button onClick={handleNext} className="btn btn-primary">
              Next
            </button>
          ) : (
            <button
              onClick={handleAddToCart}
              className="btn btn-primary"
              disabled={uploading || !image || !selectedSize || !completedCrop}
            >
              {uploading && <span className="btn-spinner" aria-hidden="true"></span>}
              {uploading ? 'Adding to Cart...' : 'Add to Cart'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomPrintWizard;
